import { CheckEmailOrgSsoConfigUser } from './../../account-api.d';
import {
  LoginUserData,
  CheckEmailDefaultUser,
  CheckEmailOrgRemoteUser,
} from '@app/account/account-api.d';
import { Observable, of, Subject, timer, throwError } from 'rxjs';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { AuthService } from '@app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from '@app/account/services/account.service';
import {
  DgCaptchaError,
  isDgCaptchaError,
} from '@app/account/models/dg-captcha-error';

// misc
import {
  catchError,
  map,
  takeUntil,
  switchMap,
  finalize,
  debounceTime,
} from 'rxjs/operators';

@Component({
  selector: 'dgx-signup-form',
  templateUrl: './signup-form.component.html',
})
export class SignupFormComponent implements AfterViewInit, OnChanges, OnInit {
  @Input() public captchaKey;
  @Input() public errors;
  @Input() public loginScope: string; // e.g. when embedded in native app
  @Input() public passedData?;
  @Input() public returnUrl;
  @Input() public specialInstructions;

  @Output()
  public preserveReturnUrl: EventEmitter<any> = new EventEmitter<any>();
  @Output() public setUtm: EventEmitter<any> = new EventEmitter<any>();

  public i18n = this.translate.instant([
    'AccountController_Continue',
    'signupForm_SignUpTitle',
    'signupForm_RegisterEmailLabel',
    'signupForm_PasswordLabel',
    'signupForm_EmailStateTooltip',
    'signupForm_InvalidEmailSignup',
    'signupForm_InvalidFirstName',
    'signupForm_InvalidPassword',
    'signupForm_CreatePasswordLabel',
    'signupForm_CreatePasswordTooltip',
    'signupForm_CreatePasswordPlaceholder',
    'signupForm_RegisterFirstNameLabel',
    'signupForm_RegisterLastNameLabel',
    'signupForm_EmailPlaceholder',
    'signupForm_FirstNamePlaceholder',
    'signupForm_LastNamePlaceholder',
    'signupForm_EmailExists',
    'signupForm_HasAccountCTA',
    'signupForm_LogInNow',
  ]);
  public user: LoginUserData = {
    email: '',
    firstname: '',
    lastname: '',
    password: '',
    username: '',
  };

  public captcha = '';
  public createPasswordTooltip = this.i18n.signupForm_CreatePasswordTooltip;
  public debounceDuration = 500;
  public emailValidationMessage = this.i18n.signupForm_InvalidEmailSignup;
  public generalMessage;
  public isValidCaptcha: boolean = true;
  public loginUrl = '';
  public saving = false;
  public signupForm = this.builder.group({
    firstname: [this.user?.firstname ?? undefined, [Validators.required]],
    email: [
      this.user?.email ?? undefined,
      [Validators.required],
      [this.validateEmail.bind(this)],
    ],
    lastname: [this.user?.lastname ?? undefined, [Validators.required]],
    password: [undefined, [Validators.required, this.checkPassword.bind(this)]],
  });
  public shouldShowCaptcha: boolean;
  public canSubmit: boolean = false;

  public get showCaptcha(): boolean {
    return this.shouldShowCaptcha;
  }

  // Hidden inputs
  public antiForgeryToken;
  public utmSource;
  public utmMedium;
  public utmCampaign;

  constructor(
    private translate: TranslateService,
    private authService: AuthService,
    private accountService: AccountService,
    private builder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.loginUrl =
      '/account/login' + this.authService.appendReturnUrl('?', this.returnUrl);
    this.i18n.signupForm_EmailExists = this.translate.instant(
      'signupForm_EmailExists',
      {
        startAnchor:
          '<a href="/account/login" class="link" data-dgat="signupForm-70a">',
        endAnchor: '</a>',
      }
    );
    this.setHiddenInputs();
    this.setUtm.emit();
    this.preserveReturnUrl.emit();
    this.handlePassedEmail();
    if (this.specialInstructions) {
      this.displayGeneral();
    }
  }

  public ngOnChanges({ passedData }: SimpleChanges) {
    if (passedData) {
      this.passedData = passedData.currentValue;
      this.user.username = this.passedData?.username;
    }
  }

  public ngAfterViewInit(): void {
    this.signupForm?.statusChanges?.subscribe();
  }

  public checkPassword(control: AbstractControl): ValidationErrors | null {
    return this.accountService.isValidPassword(control.value)
      ? null
      : { invalidPassword: true };
  }

  public displayGeneral() {
    const message =
      this.specialInstructions !== ''
        ? this.translate.instant(`AccountSvc_${this.specialInstructions}`)
        : undefined;
    this.generalMessage = message;
  }

  public focusField(elementId: string) {
    const element = document.getElementById(elementId);
    if (element) {
      element.focus();
    }
  }

  public handlePassedEmail() {
    this.user.email = (location.search.split('email=')[1] || '').split('&')[0];
    if (this.user.email.length) {
      setTimeout(() => {
        this.focusField('firstname');
      });
    }
  }

  public recaptchaSuccess(captchaString: string) {
    this.isValidCaptcha = true;
    this.captcha = captchaString;
    this.signupForm.patchValue({
      email: this.signupForm.get('email').value,
    });
    this.signupForm.updateValueAndValidity();
  }

  public recaptchaExpired() {
    this.isValidCaptcha = false;
  }

  public submit($event: Event) {
    if (this.signupForm.status !== 'VALID') {
      this.saving = false;
      $event.preventDefault();
      return false;
    } else {
      this.saving = true;
    }

    // form submits after reaching here
  }

  public validateEmail(
    control: AbstractControl
  ): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    if (this.shouldShowCaptcha && !this.isValidCaptcha) {
      return;
    }
    const validEmail =
      control.value && this.accountService.isValidEmail(control.value);
    let accountExists;
    if (validEmail) {
      const captchaToken = this.shouldShowCaptcha ? this.captcha : null;

      return this.accountService
        .checkEmail(
          control.value,
          captchaToken,
          this.loginScope,
          this.returnUrl
        )
        .pipe(
          debounceTime(this.debounceDuration),
          // There can be 3 different types of return data from checkEmail api.
          map(
            (
              response: CheckEmailDefaultUser &
                CheckEmailOrgRemoteUser &
                CheckEmailOrgSsoConfigUser
            ) => {
              if (response) {
                if (response.message) {
                  return { invalidEmail: true };
                } else {
                  accountExists =
                    response && (response.accountExists || response.name);
                  if (accountExists) {
                    this.emailValidationMessage = this.i18n.signupForm_EmailExists;
                  }
                  return accountExists ? { existingEmail: true } : null;
                }
              } else {
                this.shouldShowCaptcha = true;
                return { securityError: true };
              }
            }
          ),
          catchError((error: DgCaptchaError | Error) => {
            // check if this is a captcha error
            if (!isDgCaptchaError(error)) {
              return throwError(error);
            }
            // get the values from the dgCaptcha error
            const { showCaptcha, validToken } = error;
            this.shouldShowCaptcha = showCaptcha;

            // if ValidToken, try validation again
            if (validToken) {
              setTimeout(() => {
                this.signupForm.patchValue({
                  email: this.signupForm.get('email').value,
                });
                this.signupForm.updateValueAndValidity();
              });
            } else {
              return of({ securityError: true });
            }
          }),
          finalize(() => {
            // Due to a bug in Angular, need to force validation to prevent
            // form status from getting stuck in 'Pending' with async validation
            setTimeout(() => {
              this.signupForm.updateValueAndValidity();
            });
          })
        );
    } else {
      this.emailValidationMessage = this.i18n.signupForm_InvalidEmailSignup;
      return of({ invalidEmail: true });
    }
  }

  private setHiddenInputs() {
    const tokenEl = document.getElementsByName(
      '__RequestVerificationToken'
    )[0] as HTMLInputElement;
    const returnUrlEl = document.getElementById(
      'returnUrl'
    ) as HTMLInputElement;
    const utmSourceEl = document.getElementById(
      'utm_source'
    ) as HTMLInputElement;
    const utmMediumEl = document.getElementById(
      'utm_medium'
    ) as HTMLInputElement;
    const utmCampaignEl = document.getElementById(
      'utm_campaign'
    ) as HTMLInputElement;
    this.antiForgeryToken = tokenEl.value;
    this.returnUrl = this.returnUrl ?? returnUrlEl.value;
    this.utmSource = utmSourceEl.value;
    this.utmMedium = utmMediumEl.value;
    this.utmCampaign = utmCampaignEl.value;
  }
}
